<template>
  <div class="carousel" @mouseenter="pauseAutoPlay" @mouseleave="resumeAutoPlay">
    <div class="carousel-container">
      <div class="carousel-track" :style="{ transform: `translateX(${translateX}px)` }">
        <div
          v-for="(banner, index) in banners"
          :key="banner.id || index"
          class="carousel-item"
          :class="{
            center: index === centerIndex,
            hovered: index === hoveredIndex
          }"
          @mouseenter="handleMouseEnter(index)"
          @mouseleave="handleMouseLeave"
          @click="handleBannerClick(banner, index)"
        >
          <img :src="banner.image" :alt="banner.title" class="carousel-img" />
          <div class="banner-overlay">
            <h3 class="banner-title">{{ banner.title }}</h3>
          </div>
        </div>
      </div>
    </div>
    <div class="carousel-dots">
      <span v-for="(banner, idx) in banners" :key="idx" :class="{ active: idx === centerIndex }" @click="go(idx)"></span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CarouselBanner',
  props: {
    banners: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      centerIndex: 0,
      hoveredIndex: -1,
      translateX: 0,
      timer: null,
      itemWidth: 0,
      isAutoPlaying: true
    }
  },
  mounted() {
    this.initCarousel();
    this.startAuto();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    clearInterval(this.timer);
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    // 初始化轮播
    initCarousel() {
      this.calculateItemWidth();
      this.updateTranslateX();
    },

    // 计算项目宽度
    calculateItemWidth() {
      this.itemWidth = window.innerWidth / 3;
    },

    // 更新translateX位置
    updateTranslateX() {
      // 确保当前项目始终在中间位置
      // 容器宽度是屏幕宽度，中心位置是容器宽度的一半减去项目宽度的一半
      const containerWidth = window.innerWidth;
      const centerPosition = containerWidth / 2 - this.itemWidth / 2;

      // 计算当前中心项目应该的位置
      // 让centerIndex对应的项目移动到中心位置
      this.translateX = centerPosition - (this.centerIndex * this.itemWidth);
    },

    // 开始自动播放
    startAuto() {
      if (!this.isAutoPlaying || !this.banners.length) return;
      this.timer = setInterval(() => {
        const nextIndex = (this.centerIndex + 1) % this.banners.length;
        console.log('[Carousel] 自动播放切换:', this.centerIndex, '->', nextIndex);
        this.centerIndex = nextIndex;
        this.updateTranslateX();
      }, 4000);
    },

    // 停止自动播放
    stopAuto() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    // 暂停自动播放
    pauseAutoPlay() {
      this.stopAuto();
    },

    // 恢复自动播放
    resumeAutoPlay() {
      this.startAuto();
    },

    // 鼠标进入处理
    handleMouseEnter(index) {
      console.log('[Carousel] 鼠标悬停项目:', index);
      this.hoveredIndex = index;
      this.centerIndex = index;
      this.updateTranslateX();
    },

    // 鼠标离开处理
    handleMouseLeave() {
      this.hoveredIndex = -1;
      // 鼠标离开后保持当前中心项目不变
    },

    // 跳转到指定项目
    go(idx) {
      console.log('[Carousel] 跳转到项目:', idx);
      this.centerIndex = idx;
      this.hoveredIndex = -1; // 清除悬停状态
      this.updateTranslateX();
    },

    // 处理banner点击
    handleBannerClick(banner, index) {
      console.log('[Carousel] 点击banner:', banner);

      // 如果点击的不是中心项目，先切换到中心
      if (index !== this.centerIndex) {
        this.go(index);
        return;
      }

      // 如果是中心项目，处理跳转
      if (banner.url) {
        if (banner.type === 'comic' && banner.url.includes('/comic/')) {
          // 提取漫画ID并跳转到漫画详情页
          const comicIdMatch = banner.url.match(/\/comic\/(\d+)/);
          if (comicIdMatch && comicIdMatch[1]) {
            this.$router.push({ name: 'ComicDetail', params: { id: comicIdMatch[1] } });
          }
        } else if (banner.url.startsWith('http')) {
          // 外部链接，新窗口打开
          window.open(banner.url, '_blank');
        } else {
          // 内部路径，直接跳转
          this.$router.push(banner.url);
        }
      }
    },

    // 处理窗口大小变化
    handleResize() {
      this.calculateItemWidth();
      this.updateTranslateX();
    }
  }
}
</script>

<style scoped>
.carousel {
  width: 100vw;
  height: 500px;
  position: relative;
  overflow: hidden;
  margin-left: calc(-50vw + 50%);
}

/* 轮播容器 */
.carousel-container {
  width: 100vw;
  height: 500px;
  position: relative;
  overflow: hidden;
  margin: 0 auto;
}

.carousel-track {
  display: flex;
  transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  height: 100%;
}

.carousel-item {
  width: calc(100vw / 3);
  height: 500px;
  position: relative;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  opacity: 0.6;
  transform: scale(0.85);
  flex-shrink: 0;
  overflow: hidden;
  margin: 0;
}

/* 中心项目样式 */
.carousel-item.center {
  opacity: 1;
  transform: scale(1.05);
  z-index: 3;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(255, 215, 0, 0.6);
}

/* 悬停效果 */
.carousel-item.hovered {
  opacity: 1;
  transform: scale(1.05);
  z-index: 2;
  box-shadow: 0 12px 45px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 215, 0, 0.4);
}

.carousel-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Banner覆盖层 */
.banner-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 30px 20px 20px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.carousel-item:hover .banner-overlay,
.carousel-item.center .banner-overlay {
  opacity: 1;
}

.banner-title {
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  text-align: center;
}
.carousel-dots {
  position: absolute;
  left: 50%;
  bottom: 20px;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  width: auto;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.carousel-dots span {
  display: block;
  width: 14px;
  height: 14px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.carousel-dots span:hover {
  background: rgba(255, 255, 255, 0.8);
  transform: scale(1.2);
}

.carousel-dots .active {
  background: var(--brand);
  border-color: var(--brand);
  transform: scale(1.3);
  box-shadow: 0 2px 8px var(--brand-soft);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .carousel {
    height: 400px;
  }

  .carousel-container {
    height: 400px;
  }

  .carousel-item {
    height: 400px;
    opacity: 0.5;
    transform: scale(0.8);
  }

  .carousel-item.center {
    transform: scale(1);
    opacity: 1;
  }

  .carousel-item.hovered {
    transform: scale(1);
    opacity: 1;
  }

  .carousel-dots span {
    width: 12px;
    height: 12px;
  }
}

@media (max-width: 480px) {
  .carousel {
    height: 300px;
  }

  .carousel-container {
    height: 300px;
  }

  .carousel-item {
    height: 300px;
    opacity: 0.4;
    transform: scale(0.75);
  }

  .carousel-item.center {
    transform: scale(0.95);
    opacity: 1;
  }

  .carousel-item.hovered {
    transform: scale(0.95);
    opacity: 1;
  }

  .carousel-dots {
    bottom: 15px;
    gap: 8px;
  }

  .carousel-dots span {
    width: 10px;
    height: 10px;
  }
}
</style> 